/**
 * Observability API (staff-only). Thin client over /v2/observability/*.
 * The server gates every route to internal super-admins.
 */

import api from '@/services/api';

export type EnrichmentProviderStat = {
    provider: string;
    found_candidates: number;
    no_match: number;
    skipped_no_key: number;
    error: number;
    total: number;
    emailsFound: number;
    avgMs: number;
    hitRate: number;
};

export type EnrichmentSummary = {
    window: { days: number; since: string };
    totals: { attempts: number; verifierCalls: number; avgMs: number };
    outcomes: Record<string, number>;
    derived: { foundRate: number; resolvedRate: number; cacheHitRate: number };
    providers: EnrichmentProviderStat[];
    tier0: { steps: number; hits: number; hitRate: number };
    verification: Record<string, number>;
    spend: {
        rates: Record<string, number>;
        byProvider: Record<string, { calls: number; estimatedUsd: number }>;
        estimatedUsd: number;
    };
    timeseries: Array<{ day: string; attempts: number; found: number; cacheHits: number; verifierCalls: number }>;
};

export type EnrichmentAttemptRow = {
    id: string;
    organizationId: string | null;
    trigger: string;
    subjectKind: string | null;
    campaignId: string | null;
    outcome: string;
    finderProvider: string | null;
    emailType: string | null;
    verificationStatus: string | null;
    steps: Array<{ provider: string; outcome: string; ms: number }>;
    verifierCalls: number;
    cacheable: boolean;
    ms: number;
    at: string;
};

export type ProviderQuota = {
    window: { days: number; since: string };
    providers: Array<{ provider: string; configured: boolean; limit: number | null; consumed: number; pctUsed: number | null }>;
    live: { millionverifier: number | null };
};

export type ObservabilityPanel = { key: string; title: string; description: string; endpoints: Record<string, string> };

// ── Search Calibration panel (Phase 6) ──────────────────────────────────────
export type CalibrationGateRejects = {
    delivered: number;
    disqualified: number;
    suitability_no: number;
    below_floor: number;
    must_pass: number;
    required_no: number;
};

export type CalibrationDiagnostics = {
    criteriaCount: number;
    scored: number;
    deliverable: number;
    preScanDropped: number;
    bottleneckCriterionIndex: number | null;
    bottleneckCriterionText: string;
    perCriterion: Array<{ index: number; text: string; good: number; partial: number; no: number; noPct: number }>;
    gateRejects: CalibrationGateRejects;
};

export type CalibrationSummary = {
    windowDays: number;
    runCount: number;
    totals: { scanned: number; scored: number; delivered: number; target: number; preScanDropped: number };
    yields: { deliverPerScored: number; deliverPerScanned: number };
    gateRejects: CalibrationGateRejects;
    bottlenecks: Array<{ criterion: string; count: number }>;
};

export type CalibrationRun = {
    id: string;
    organizationId: string;
    jobId: string;
    jobSearchId: string;
    status: string;
    trigger: string;
    targetCount: number;
    scannedCount: number;
    scoredCount: number;
    deliveredCount: number;
    exhaustionReason: string;
    createdAt: string;
    finishedAt: string | null;
    diagnostics: CalibrationDiagnostics | null;
};

export type CalibrationContractRow = {
    id: string;
    jobSearchId: string;
    version: number;
    source: 'synth' | 'edit';
    generatedAt: string;
    mustPass: string[];
    gatedCriteria: number;
    antiPatterns: string[];
    learnedPreferenceSummary: string | null;
};

export const observabilityApi = {
    panels: () => api.get<{ panels: ObservabilityPanel[] }>('/v2/observability/panels'),
    enrichmentSummary: (days = 7) => api.get<EnrichmentSummary>('/v2/observability/enrichment/summary', { params: { days } }),
    enrichmentAttempts: (limit = 50) => api.get<{ attempts: EnrichmentAttemptRow[] }>('/v2/observability/enrichment/attempts', { params: { limit } }),
    enrichmentQuota: (days = 7) => api.get<ProviderQuota>('/v2/observability/enrichment/quota', { params: { days } }),
    calibrationSummary: (days = 14) => api.get<CalibrationSummary>('/v2/observability/calibration/summary', { params: { days } }),
    calibrationRuns: (limit = 30) => api.get<{ runs: CalibrationRun[] }>('/v2/observability/calibration/runs', { params: { limit } }),
    calibrationContracts: (limit = 30) => api.get<{ contracts: CalibrationContractRow[] }>('/v2/observability/calibration/contracts', { params: { limit } }),
};
